import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { api, api_patch } from '@/services/api.service'

import InfoTip from './InfoTip'
import { useProviderSession } from './ProviderSession'
import { ProviderTeamMember } from './ProviderTeam'

export type ClientStatus = 'active' | 'onboarding' | 'at_risk'
export type EngagementLevel = 'high' | 'medium' | 'low' | 'insufficient'

interface ProviderFamily {
    licenseId: string
    email: string
    childName: string | null
    parentName: string | null
    status: ClientStatus
    engagementLevel: EngagementLevel
    lastActiveAt: string | null
    assignedTo: string | null
    assignedToName: string | null
}

interface ProviderFamilyListResponse {
    families: ProviderFamily[]
    summary: { total: number; active: number; onboarding: number; atRisk: number }
}

interface FormerPatient {
    licenseId: string
    email: string
    childName: string | null
    parentName: string | null
    status: string
    activatedAt: string | null
    endedAt: string | null
}

function formatDate(value?: string | null) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.valueOf())) return null
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const STATUS_BADGE: Record<ClientStatus, { label: string; cls: string }> = {
    active: { label: 'Active', cls: 'bg-green-50 text-green-700 border-green-200' },
    onboarding: { label: 'Onboarding', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    at_risk: { label: 'At risk', cls: 'bg-red-50 text-red-700 border-red-200' },
}

const ENGAGEMENT_BADGE: Record<EngagementLevel, { label: string; dot: string; text: string }> = {
    high: { label: 'High', dot: 'bg-green-500', text: 'text-neutral-700' },
    medium: { label: 'Medium', dot: 'bg-amber-500', text: 'text-neutral-700' },
    low: { label: 'Low', dot: 'bg-red-500', text: 'text-neutral-700' },
    insufficient: { label: 'Not enough data yet', dot: 'bg-neutral-300', text: 'text-neutral-400' },
}

export default function ProviderFamilies() {
    const session = useProviderSession()
    const isAdmin = session?.role === 'admin'
    const isCoordinator = session?.role === 'coordinator'
    // Admins and coordinators add clients by inviting families from the Licenses
    // page; the read-only viewer role cannot invite, so it gets no call to action.
    const canInvite = isAdmin || isCoordinator
    const [data, setData] = useState<ProviderFamilyListResponse | null>(null)
    const [former, setFormer] = useState<FormerPatient[]>([])
    const [coordinators, setCoordinators] = useState<{ userId: string; name: string }[]>([])
    const [assignError, setAssignError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')

    useEffect(() => {
        let cancelled = false
        async function load() {
            const response = await api<ProviderFamilyListResponse>(ROUTES.provider.family)
            if (!cancelled && response.status && response.status < 400 && response.data) {
                setData(response.data)
            }
            if (isAdmin) {
                const [formerRes, memberRes] = await Promise.all([
                    api<{ former: FormerPatient[] }>(`${ROUTES.provider.family}/former`),
                    api<{ members: ProviderTeamMember[] }>(ROUTES.provider.member),
                ])
                if (!cancelled) {
                    setFormer(formerRes.data?.former ?? [])
                    setCoordinators(
                        (memberRes.data?.members ?? [])
                            .filter((m) => m.role === 'coordinator')
                            .map((m) => ({ userId: m.userId, name: m.name || m.email || 'Coordinator' }))
                    )
                }
            }
            if (!cancelled) setLoading(false)
        }
        load()
        return () => {
            cancelled = true
        }
    }, [isAdmin])

    async function handleAssign(licenseId: string, coordinatorId: string) {
        setAssignError(null)
        const response = await api_patch<{ assignedTo: string | null; assignedToName: string | null }>(
            `${ROUTES.provider.family}/${licenseId}/assign`,
            { coordinatorId: coordinatorId || null }
        )
        if (response.status && response.status >= 400) {
            setAssignError(response.error || 'Failed to update caseload assignment')
            return
        }
        setData((prev) =>
            prev
                ? {
                      ...prev,
                      families: prev.families.map((f) =>
                          f.licenseId === licenseId
                              ? {
                                    ...f,
                                    assignedTo: response.data?.assignedTo ?? null,
                                    assignedToName: response.data?.assignedToName ?? null,
                                }
                              : f
                      ),
                  }
                : prev
        )
    }

    const families = data?.families ?? []
    const summary = data?.summary ?? { total: 0, active: 0, onboarding: 0, atRisk: 0 }
    const q = search.trim().toLowerCase()
    const filtered = q
        ? families.filter(
              (f) =>
                  (f.childName ?? '').toLowerCase().includes(q) ||
                  (f.parentName ?? '').toLowerCase().includes(q) ||
                  f.email.toLowerCase().includes(q)
          )
        : families

    return (
        <div className='max-w-6xl mx-auto px-4 sm:px-6 py-8'>
            <div className='mb-6'>
                <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>Clients</h1>
                <p className='text-sm text-neutral-600 mt-1'>
                    {`${summary.total} clients ${isCoordinator ? 'on your caseload' : 'across your organization'}. ${summary.active} active, ${summary.onboarding} onboarding, ${summary.atRisk} at risk.`}
                </p>
                {canInvite && (
                    <p className='text-sm text-neutral-500 mt-2'>
                        To add a client, invite their family from the{' '}
                        <Link to='/licenses' className='font-medium text-brand-600 hover:underline'>
                            Licenses
                        </Link>{' '}
                        page. They appear here once they create their account.
                    </p>
                )}
            </div>

            {assignError && (
                <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800'>
                    {assignError}
                </div>
            )}

            <div className='relative mb-4'>
                <MagnifyingGlassIcon className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400' />
                <input
                    type='text'
                    placeholder='Search by child name, parent, or email'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className='w-full max-w-md pl-9 pr-3 py-2 border border-neutral-300 rounded-lg text-sm'
                />
            </div>

            <div className='bg-white border border-neutral-200 rounded-xl overflow-hidden'>
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                        <thead className='bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wider'>
                            <tr>
                                <th className='px-4 py-3 text-left font-medium'>Client</th>
                                <th className='px-4 py-3 text-left font-medium'>
                                    Status
                                    <InfoTip text='Active: engaging regularly. Onboarding: still completing setup and early milestones. At risk: no meaningful app activity in 14+ days, or incomplete onboarding.' />
                                </th>
                                <th className='px-4 py-3 text-left font-medium'>
                                    Engagement
                                    <InfoTip text='A High / Medium / Low rating from how recently the family was last active in the app.' />
                                </th>
                                <th className='px-4 py-3 text-left font-medium'>Last active</th>
                                {!isCoordinator && <th className='px-4 py-3 text-left font-medium'>Coordinator</th>}
                                <th className='px-4 py-3 text-right font-medium'></th>
                            </tr>
                        </thead>
                        <tbody className='divide-y divide-neutral-100'>
                            {filtered.map((c) => (
                                <tr key={c.licenseId} className='hover:bg-neutral-50/50'>
                                    <td className='px-4 py-3'>
                                        <div className='font-medium text-neutral-900'>
                                            {c.childName ?? c.email}
                                        </div>
                                        <div className='text-xs text-neutral-500'>
                                            {c.parentName ? `${c.parentName} · ` : ''}
                                            {c.email}
                                        </div>
                                    </td>
                                    <td className='px-4 py-3'>
                                        <span
                                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_BADGE[c.status].cls}`}
                                        >
                                            {STATUS_BADGE[c.status].label}
                                        </span>
                                    </td>
                                    <td className='px-4 py-3'>
                                        <span className='inline-flex items-center gap-1.5 text-xs'>
                                            <span className={`w-2 h-2 rounded-full ${ENGAGEMENT_BADGE[c.engagementLevel].dot}`} />
                                            <span className={ENGAGEMENT_BADGE[c.engagementLevel].text}>
                                                {ENGAGEMENT_BADGE[c.engagementLevel].label}
                                            </span>
                                        </span>
                                    </td>
                                    <td className='px-4 py-3 text-neutral-600'>
                                        {formatDate(c.lastActiveAt) ?? 'Never'}
                                    </td>
                                    {!isCoordinator && (
                                        <td className='px-4 py-3'>
                                            {isAdmin ? (
                                                <select
                                                    value={c.assignedTo ?? ''}
                                                    onChange={(e) => handleAssign(c.licenseId, e.target.value)}
                                                    className='px-2 py-1.5 border border-neutral-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-brand-500'
                                                >
                                                    <option value=''>Unassigned</option>
                                                    {coordinators.map((coord) => (
                                                        <option key={coord.userId} value={coord.userId}>
                                                            {coord.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className='text-xs text-neutral-600'>
                                                    {c.assignedToName ?? 'Unassigned'}
                                                </span>
                                            )}
                                        </td>
                                    )}
                                    <td className='px-4 py-3 text-right'>
                                        <Link
                                            to={`/clients/${c.licenseId}`}
                                            className='text-sm text-brand-600 hover:underline'
                                        >
                                            View
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {!loading && filtered.length === 0 && (
                    <div className='py-12 px-6 text-center text-sm text-neutral-500'>
                        {families.length !== 0 ? (
                            'No clients match this search.'
                        ) : canInvite ? (
                            <div className='max-w-md mx-auto'>
                                <p className='font-medium text-neutral-700'>
                                    {isCoordinator ? 'No clients on your caseload yet' : 'No clients yet'}
                                </p>
                                <p className='mt-1'>
                                    {isCoordinator
                                        ? 'Invite a family from the Licenses page to add one to your caseload. They appear here once they create their account. An admin can also assign existing clients to you.'
                                        : 'Invite a family from the Licenses page to add your first client. They appear here once they create their account.'}
                                </p>
                                <Link
                                    to='/licenses'
                                    className='inline-flex items-center mt-4 px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors'
                                >
                                    Go to Licenses
                                </Link>
                            </div>
                        ) : (
                            'No registered clients yet. Clients appear here once an invited family creates their account.'
                        )}
                    </div>
                )}
                {loading && <div className='py-12 text-center text-sm text-neutral-500'>Loading…</div>}
            </div>

            {/* Former clients — sponsorship ended; org records preserved */}
            {isAdmin && former.length > 0 && (
                <div className='mt-8'>
                    <h2 className='text-lg font-semibold text-neutral-900 mb-1'>Former clients</h2>
                    <p className='text-sm text-neutral-600 mb-4'>
                        Sponsorships that have ended. Your team&apos;s notes and homework records are preserved;
                        the client&apos;s app activity is no longer visible.
                    </p>
                    <div className='bg-white border border-neutral-200 rounded-xl overflow-hidden'>
                        <div className='overflow-x-auto'>
                            <table className='w-full text-sm'>
                                <thead className='bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wider'>
                                    <tr>
                                        <th className='px-4 py-3 text-left font-medium'>Client</th>
                                        <th className='px-4 py-3 text-left font-medium'>Status</th>
                                        <th className='px-4 py-3 text-left font-medium'>Sponsored</th>
                                        <th className='px-4 py-3 text-right font-medium'>&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody className='divide-y divide-neutral-100'>
                                    {former.map((c) => (
                                        <tr key={c.licenseId} className='hover:bg-neutral-50/50'>
                                            <td className='px-4 py-3'>
                                                <div className='font-medium text-neutral-900'>
                                                    {c.childName ?? c.email}
                                                </div>
                                                <div className='text-xs text-neutral-500'>
                                                    {c.parentName ? `${c.parentName} · ` : ''}
                                                    {c.email}
                                                </div>
                                            </td>
                                            <td className='px-4 py-3'>
                                                <span className='inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-200 text-neutral-700 capitalize'>
                                                    {c.status}
                                                </span>
                                            </td>
                                            <td className='px-4 py-3 text-neutral-600'>
                                                {formatDate(c.activatedAt) ?? '—'} – {formatDate(c.endedAt) ?? '—'}
                                            </td>
                                            <td className='px-4 py-3 text-right'>
                                                <Link
                                                    to={`/clients/former/${c.licenseId}`}
                                                    className='text-sm text-neutral-600 hover:underline'
                                                >
                                                    View
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
